import type { UsageDetails } from './metering.ts';

/**
 * The `usage_details_json` blocks platform rating never reads (#2539 step 8,
 * "usage_events keeps only what rating needs"). The gateway writes the same
 * document twice per request — `usage_logs` (request detail: ops, the
 * allocator's procurement recompute, the archive) and `usage_events` (the
 * metering outbox rating prices from). With `USAGE_EVENTS_DETAILS_PROJECTION=1`
 * the outbox copy drops these top-level keys. The `usage_logs` copy keeps all
 * of them unless its own switch, `USAGE_LOGS_DETAILS_PROJECTION`, is on
 * (`usage-logs-details-projection.ts`: it keeps admission, affinity and
 * segments, and raw_usage / audit only on the rows its readers need).
 *
 * Why each one is safe to drop from the RATING input (every reader of
 * `usage_events.usage_details_json` is platform rating and its replay tool —
 * `EVENT_COLUMNS` in internal-cron-rate-events.ts, `replay-diff.ts`):
 *
 * - `raw_usage`: forensic upstream usage snippets. Rating's parser
 *   (`parseUsageDetailsWithNotes` → `isUsageDetails`) and the pricing kernel
 *   (`computeCostBreakdownUsdMicros`) never read it.
 * - `segments`: per-response summaries for CLI probes and SSE; not read.
 * - `audit`: service tier / inference geo / iterations / server-tool flag;
 *   not read (the token tier is `pricing_variant_key`, the surcharges are
 *   `unit_surcharges`).
 * - `admission`, `affinity`: documented as ignored by rating (see their
 *   declarations in metering.ts).
 *
 * A DENY list, not an allow list, on purpose: a top-level key added later is
 * KEPT until someone decides it is unread, so a new priced field can never be
 * silently stripped from the pricing input. The price-relevant keys rating
 * reads today — `schema_version`, `profile`, `protocol`, `display`,
 * `billable`, `invalid_dimensions`, `unit_usage`, `unit_surcharges`,
 * `pricing_variant_key` — are kept by construction (none is listed here), and
 * `display` / `billable` must stay: the parser rejects a document without
 * them and the event would fall back to the legacy four-bucket path, a silent
 * reprice.
 *
 * Only `billing_anomalies.usage_details_json` sees the difference: rating
 * copies the event's document there when it files a procurement
 * `cost_overflow` anomaly. That row keeps its `request_id`; the full document
 * is on the `usage_logs` row of the same request while that row lives, and in
 * the usage_logs OSS archive after the JP reaper removed it (ops request
 * detail hydrates it from there) — minus `display`, `audit` and most
 * `raw_usage` when `USAGE_LOGS_DETAILS_PROJECTION` is on too.
 */
export const USAGE_DETAILS_UNRATED_KEYS = [
  'raw_usage',
  'segments',
  'audit',
  'admission',
  'affinity',
] as const;

const UNRATED_KEYS: ReadonlySet<string> = new Set(USAGE_DETAILS_UNRATED_KEYS);

export type UsageDetailsUnratedKey = (typeof USAGE_DETAILS_UNRATED_KEYS)[number];

/**
 * The usage-details document as pricing may see it: {@link UsageDetails}
 * without {@link USAGE_DETAILS_UNRATED_KEYS}. It is the type of rating's
 * parsed document (`parseUsageDetailsWithNotes`) and of the contracts pricing
 * kernel's `usageDetails` input, so code that prices from one of the dropped
 * blocks (say `usageDetails.audit?.inference_geo`) does not compile.
 *
 * TO PRICE FROM ONE OF THOSE BLOCKS: first take its key out of
 * `USAGE_DETAILS_UNRATED_KEYS` (which puts it back on this type), and make
 * sure no gateway still writes projected `usage_events` rows — every
 * `USAGE_EVENTS_DETAILS_PROJECTION` must be off, or the new key must have
 * shipped on every gateway — before the pricing change deploys. Otherwise the
 * outbox rows rating reads do not carry the field and the price silently
 * drops. `platform-api/test/rating-unrated-keys-scan.test.ts` backs this up
 * for code that reaches the blocks through a cast.
 */
export type RatedUsageDetails = Omit<UsageDetails, UsageDetailsUnratedKey>;

/**
 * The rating projection of a usage-details document: the same object minus
 * {@link USAGE_DETAILS_UNRATED_KEYS}, every other key kept in its original
 * order with its original value (nested values are shared, not copied — the
 * caller serialises immediately). Never mutates the input. Anything that is
 * not a plain object is returned as is: rating already treats such a
 * document as unparseable, and projecting must not turn one shape into
 * another.
 */
export function projectUsageDetailsForRating(details: UsageDetails): RatedUsageDetails {
  if (typeof details !== 'object' || details === null || Array.isArray(details)) {
    return details;
  }
  const kept = Object.entries(details).filter(([key]) => !UNRATED_KEYS.has(key));
  if (kept.length === Object.keys(details).length) {
    return details;
  }
  return Object.fromEntries(kept) as unknown as RatedUsageDetails;
}
